/** @format */
import { RefObject, useEffect, useRef, useState } from 'react';

export const useScroll = (
    ref: RefObject<HTMLElement | Window>,
    handler: (event: any) => void
) => {
    const dynamicHandler = useRef(handler);

    // Throttle scroll events to animation frames
    let ticking = false;
    function listener(event: Event) {
        if (!ticking) {
            window.requestAnimationFrame(function () {
                dynamicHandler?.current?.(event);
                ticking = false;
            });
            ticking = true;
        }
    }

    useEffect(() => {
        dynamicHandler.current = handler;
    }, [handler]);

    useEffect(() => {
        ref.current && ref.current.addEventListener('scroll', listener);

        return () => {
            ref.current && ref.current.removeEventListener('scroll', listener);
        };
    }, [ref.current]); // Recall only if ref changes
};

export enum ScrollDirection {
    UP = 'up',
    DOWN = 'down',
    NONE = '',
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface ScrollState {
    x: number;
    y: number;
    direction: ScrollDirection;
}

export const useScrollInfo = () => {
    const [scroll, setScroll] = useState<ScrollState>({
        x: typeof window !== 'undefined' ? window.pageXOffset : 0,
        y: typeof window !== 'undefined' ? window.pageYOffset : 0,
        direction: ScrollDirection.NONE,
    });

    function listener(): void {
        setScroll((prev: ScrollState) => ({
            x: window.pageXOffset,
            y: window.pageYOffset,
            direction:
                prev.y > window.pageYOffset
                    ? ScrollDirection.UP
                    : ScrollDirection.DOWN,
        }));
    }

    useEffect(() => {
        window.addEventListener('scroll', listener);
        return () => window.removeEventListener('scroll', listener);
    }, []);

    return scroll;
};
